import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export default function Admin() {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=logged
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [tab, setTab] = useState("bookings");

  const authOpts = { withCredentials: true };

  const loadData = async (retried = false) => {
    try {
      const [b, c, cb] = await Promise.all([
        api.get("/admin/bookings", authOpts),
        api.get("/admin/contacts", authOpts),
        api.get("/admin/callbacks", authOpts),
      ]);
      setBookings(b.data);
      setContacts(c.data);
      setCallbacks(cb.data);
    } catch {
      if (!retried) {
        try {
          await api.post("/auth/refresh", {}, authOpts);
          return loadData(true);
        } catch {
          // fallthrough
        }
      }
      toast.error("Sessione scaduta");
      setUser(false);
    }
  };

  useEffect(() => {
    api.get("/auth/me", authOpts)
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/auth/login", { email, password }, authOpts);
      setUser(res.data);
      setPassword("");
      toast.success("Accesso effettuato");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Credenziali non valide");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status }, authOpts);
      toast.success("Stato aggiornato");
      loadData();
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout", {}, authOpts);
    } catch {
      // ignore
    }
    setUser(false);
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#F6F8F3] flex items-center justify-center">
        <p className="text-[#73706E] text-sm">Caricamento…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F6F8F3] flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-sm bg-white border border-[#CFD9C5] rounded-3xl p-10" data-testid="admin-login-form">
          <h1 className="font-serif-elegant text-3xl text-[#3D3B3A]">Area riservata</h1>
          <p className="text-sm text-[#73706E] mt-2">Accedi per gestire prenotazioni e messaggi.</p>
          <div className="mt-8">
            <Label htmlFor="email" className="text-xs text-[#73706E]">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 bg-transparent border-0 border-b border-[#CFD9C5] rounded-none px-0 focus-visible:ring-0"
              data-testid="admin-email"
              required
            />
          </div>
          <div className="mt-6">
            <Label htmlFor="pwd" className="text-xs text-[#73706E]">Password</Label>
            <Input
              id="pwd"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 bg-transparent border-0 border-b border-[#CFD9C5] rounded-none px-0 focus-visible:ring-0"
              data-testid="admin-password"
              required
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="mt-8 w-full bg-[#9DB39A] hover:bg-[#7E9C7B] text-white rounded-full py-3 text-sm transition"
          >
            Accedi
          </button>
          <Link to="/" className="mt-6 block text-center text-xs text-[#73706E] underline">Torna al sito</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8F3] py-16 px-6" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-serif-elegant text-4xl text-[#3D3B3A]">Dashboard</h1>
            <p className="text-sm text-[#73706E] mt-1">Prenotazioni e messaggi ricevuti</p>
          </div>
          <button onClick={logout} className="text-sm text-[#73706E] hover:text-[#3D3B3A]" data-testid="admin-logout">
            Esci
          </button>
        </div>

        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setTab("bookings")}
            data-testid="admin-tab-bookings"
            className={`px-5 py-2 rounded-full text-sm ${tab === "bookings" ? "bg-[#9DB39A] text-white" : "bg-white border border-[#CFD9C5] text-[#3D3B3A]"}`}
          >
            Prenotazioni ({bookings.length})
          </button>
          <button
            onClick={() => setTab("contacts")}
            data-testid="admin-tab-contacts"
            className={`px-5 py-2 rounded-full text-sm ${tab === "contacts" ? "bg-[#9DB39A] text-white" : "bg-white border border-[#CFD9C5] text-[#3D3B3A]"}`}
          >
            Messaggi ({contacts.length})
          </button>
          <button
            onClick={() => setTab("callbacks")}
            data-testid="admin-tab-callbacks"
            className={`px-5 py-2 rounded-full text-sm ${tab === "callbacks" ? "bg-[#9DB39A] text-white" : "bg-white border border-[#CFD9C5] text-[#3D3B3A]"}`}
          >
            Richiami ({callbacks.length})
          </button>
        </div>

        {tab === "bookings" && (
          <div className="bg-white border border-[#CFD9C5] rounded-3xl overflow-hidden">
            {bookings.length === 0 ? (
              <p className="p-10 text-[#73706E] text-center">Nessuna prenotazione ricevuta.</p>
            ) : (
              <table className="w-full text-sm" data-testid="admin-bookings-table">
                <thead className="bg-[#E9EFE4] text-[#3D3B3A]">
                  <tr>
                    <th className="text-left p-4">Ricevuta il</th>
                    <th className="text-left p-4">Preferenza</th>
                    <th className="text-left p-4">Nome</th>
                    <th className="text-left p-4">Contatto</th>
                    <th className="text-left p-4">Motivo</th>
                    <th className="text-left p-4">Stato</th>
                    <th className="text-left p-4">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-[#CFD9C5]" data-testid={`booking-row-${b.id}`}>
                      <td className="p-4">{format(new Date(b.created_at), "d MMM yyyy", { locale: it })}</td>
                      <td className="p-4">{b.preference || (b.date ? `${b.date} ${b.slot}` : "—")}</td>
                      <td className="p-4">{b.name}</td>
                      <td className="p-4 text-[#73706E]">
                        <div>{b.email}</div>
                        <div>{b.phone}</div>
                      </td>
                      <td className="p-4 max-w-[220px] text-[#73706E]">{b.note || "—"}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs ${
                          b.status === "confirmed" ? "bg-green-100 text-green-800" :
                          b.status === "cancelled" ? "bg-red-100 text-red-800" :
                          "bg-amber-100 text-amber-800"
                        }`}>{b.status}</span>
                      </td>
                      <td className="p-4 space-x-2">
                        <button onClick={() => updateStatus(b.id, "confirmed")} className="text-xs text-green-700 hover:underline">Conferma</button>
                        <button onClick={() => updateStatus(b.id, "cancelled")} className="text-xs text-red-700 hover:underline">Annulla</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-4" data-testid="admin-contacts-list">
            {contacts.length === 0 ? (
              <p className="p-10 text-[#73706E] text-center bg-white rounded-3xl border border-[#CFD9C5]">Nessun messaggio ricevuto.</p>
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="bg-white border border-[#CFD9C5] rounded-2xl p-6" data-testid={`contact-row-${c.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium text-[#3D3B3A]">{c.name}</p>
                      <p className="text-sm text-[#73706E]">{c.email} {c.phone && `· ${c.phone}`}</p>
                    </div>
                    <p className="text-xs text-[#73706E]">{format(new Date(c.created_at), "d MMM yyyy HH:mm", { locale: it })}</p>
                  </div>
                  <p className="text-sm text-[#3D3B3A] whitespace-pre-line">{c.message}</p>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "callbacks" && (
          <div className="space-y-4" data-testid="admin-callbacks-list">
            {callbacks.length === 0 ? (
              <p className="p-10 text-[#73706E] text-center bg-white rounded-3xl border border-[#CFD9C5]">Nessuna richiesta di richiamo.</p>
            ) : (
              callbacks.map((c) => (
                <div key={c.id} className="bg-white border border-[#CFD9C5] rounded-2xl p-6" data-testid={`callback-row-${c.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium text-[#3D3B3A]">{c.name}</p>
                      <p className="text-sm text-[#73706E]">
                        <a href={`tel:${c.phone}`} className="hover:text-[#7E9C7B]">{c.phone}</a>
                        {c.preferred_time && ` · preferito: ${c.preferred_time}`}
                      </p>
                    </div>
                    <p className="text-xs text-[#73706E]">{format(new Date(c.created_at), "d MMM yyyy HH:mm", { locale: it })}</p>
                  </div>
                  {c.note && <p className="text-sm text-[#3D3B3A] whitespace-pre-line">{c.note}</p>}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
